import { useEffect, useMemo, useRef, useState } from "react";
import { queryClient } from "@/client";
import clsx from "clsx";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { HiOutlineMagnifyingGlass } from "react-icons/hi2";

import { useDismissUnmatchedAlert } from "@/hooks/api/list/enrich/useDismissUnmatchedAlert";
import { useInfiniteUnmatchedItems } from "@/hooks/api/list/enrich/useInfiniteUnmatchedItems";
import { useMatchUnmatched } from "@/hooks/api/list/enrich/useMatchUnmatched";
import { listDetailKey } from "@/hooks/api/list/useGetList";
import useDebounceQuery from "@/hooks/useDebounceQuery";
import useInfiniteScroll from "@/hooks/useInfiniteScroll";
import Button from "@/components/atoms/Button";
import Input from "@/components/atoms/input/Input";
import EmptyModalContent from "@/components/atoms/modal-panels/EmptyModalContent";
import FeaturedText from "@/components/molecules/FeaturedText";
import Modal from "@/components/molecules/Modal";
import LeaveUnmatchedItemsModal from "@/components/molecules/modals/matching/LeaveUnmatchedItemsModal";
import MatchUnmatchedRowItem from "@/components/organisms/enrich/MatchUnmatchedRowItem";
import MatchUnmatchedRowItemsSkeleton from "@/components/organisms/enrich/MatchUnmatchedRowItemsSkeleton";

export type MapUnmatchedItemsModal = {
  onClose: () => void;
  onSuccess: () => void;
  preventClose?: boolean;
  listId: string;
  alertIsShown?: boolean;
};

export type UnmatchedFormValues = {
  items: Record<
    string,
    {
      id: string;
      name: string;
    } | null
  >;
};

const MapUnmatchedItemsModal = ({
  onClose,
  preventClose = false,
  onSuccess,
  listId,
  alertIsShown = false,
}: MapUnmatchedItemsModal) => {
  const { t } = useTranslation();
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { query, setDebounceQuery, isTyping } = useDebounceQuery();
  const { dismissUnmatchedAlert } = useDismissUnmatchedAlert();
  const { matchUnmatched, isPending: matchUnmatchedIsLoading } = useMatchUnmatched();

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteUnmatchedItems({
    listId,
    query,
  });
  const observerRef = useInfiniteScroll({ hasNextPage, fetchNextPage, containerRef });
  const flattenedData = useMemo(() => data?.pages.flatMap((page) => page.data) || [], [data]);

  const defaultValues = useMemo(
    () => flattenedData.reduce((acc, item) => ({ ...acc, [item.id]: null }), {}),
    [flattenedData],
  );
  const methods = useForm<UnmatchedFormValues>({
    defaultValues: {
      items: defaultValues,
    },
  });
  const { handleSubmit, getValues, setValue, watch, formState, unregister } = methods;
  const formWatch = watch("items");

  const isInitialLoading = useMemo(() => isLoading && flattenedData.length === 0, [isLoading, flattenedData]);
  const hasNoResults = useMemo(() => query && flattenedData.length === 0, [query, flattenedData]);
  const hasData = useMemo(() => flattenedData.length > 0, [flattenedData]);

  const somethingIsSelected = () => {
    const vals = getValues("items");
    if (!vals) return false;
    return Object.values(vals).some((value) => value !== null && value !== undefined);
  };

  const onSubmit = (data: UnmatchedFormValues) => {
    if (somethingIsSelected()) {
      matchUnmatched({ listId, items: data.items }, { onSuccess: () => onSuccess() });
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (alertIsShown) {
      dismissUnmatchedAlert({ listId });
    }
  }, [alertIsShown]);

  useEffect(() => {
    toast.dismiss("unmatched-alert");
  }, []);

  // Close modal if no data is present
  useEffect(() => {
    if (query === "" && flattenedData.length === 0 && formWatch === undefined) {
      onClose();
      // Refetch list details
      queryClient.invalidateQueries({ queryKey: listDetailKey(listId) });
    }
  }, [flattenedData, query, formWatch]);

  const _onClose = () => {
    if (somethingIsSelected()) {
      setIsOpened(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <EmptyModalContent preventClose={preventClose} onClose={() => _onClose()} padding={"sm"}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={"flex h-full max-h-[85vh] min-h-[85vh] flex-col gap-4 rounded-2xl bg-primary p-10"}>
              <div>
                <h2 className={"mb-1 text-xl font-semibold text-primary"}>{t("unmatched.heading")}</h2>
                <p className={"max-w-[65%] text-sm text-quaternary"}>{t("unmatched.description")}</p>
              </div>

              <div className={"flex items-center justify-between"}>
                <h3 className={"font-semibold"}>{t("unmatched.unmapped")}</h3>
                <div>
                  <Input
                    leftPlaceholder={<HiOutlineMagnifyingGlass className={"size-5"} />}
                    onChange={(e) => setDebounceQuery(e.target.value)}
                    placeholder={t("search.search")}
                    sizing={"xs"}
                  />
                </div>
              </div>

              <div className={"rounded-lg bg-secondary"}>
                <div className={"grid grid-cols-2 px-4 py-3 text-sm font-medium"}>
                  <h3>{t("unmatched.yourData")}</h3>
                  <h3>{t("unmatched.mappedData")}</h3>
                </div>
              </div>

              <div
                className={clsx(
                  "h-full flex-1 overflow-y-auto",
                  !isInitialLoading && !isFetchingNextPage && !isTyping && hasNoResults && "flex items-center",
                )}
                ref={containerRef}
              >
                {isInitialLoading ? (
                  <MatchUnmatchedRowItemsSkeleton count={4} />
                ) : (
                  hasData &&
                  flattenedData.map((item) => (
                    <MatchUnmatchedRowItem
                      key={item.id}
                      listId={listId}
                      setValue={setValue}
                      unmatchedItem={item}
                      unregister={unregister}
                    />
                  ))
                )}

                {hasNoResults && (
                  <FeaturedText
                    featuredIcon={{
                      color: "primary",
                      icon: HiOutlineMagnifyingGlass,
                      size: "xl",
                    }}
                    heading={t("unmatched.nothingFound.title")}
                    body={t("unmatched.nothingFound.description", { query })}
                  />
                )}

                {((isFetchingNextPage && !isInitialLoading) || isTyping) && (
                  <MatchUnmatchedRowItemsSkeleton count={4} />
                )}

                <div ref={observerRef} />
              </div>

              <div className="mx-auto mt-4 grid w-full min-w-96 max-w-[70%] grid-cols-2 gap-3">
                <Button
                  type={"button"}
                  label={t("cancel")}
                  onClick={() => _onClose()}
                  color="natural"
                  variants="outline"
                />
                <Button
                  type={"submit"}
                  label={t("unmatched.addMapped")}
                  color="brand"
                  variants="primary"
                  isLoading={matchUnmatchedIsLoading || formState.isSubmitting}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </EmptyModalContent>
      <Modal
        isOpen={isOpened}
        onClose={() => setIsOpened(false)}
        content={LeaveUnmatchedItemsModal}
        contentProps={{
          onSuccess: () => {
            setIsOpened(false);
            onSuccess();
          },
        }}
      />
    </>
  );
};

export default MapUnmatchedItemsModal;
